import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PoultryData } from "@/components/poultry/types";

export const usePoultryTransaction = (onSuccess: () => void) => {
  const [selectedPoultry, setSelectedPoultry] = useState<any[]>([]);
  const [isCredit, setIsCredit] = useState(false);
  const [partialPayment, setPartialPayment] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerContact, setCustomerContact] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [customerSignature, setCustomerSignature] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  const calculateTotal = () => {
    return selectedPoultry.reduce((total, item) => {
      const price = typeof item.data.price === 'string' 
        ? parseFloat(item.data.price.replace(/[^\d.-]/g, ''))
        : (item.data.price || 0);
      return total + price * (item.selectedQuantity || 0);
    }, 0);
  };

  const handleAddTransaction = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const totalAmount = calculateTotal();
      const paidAmount = isCredit ? parseFloat(partialPayment || "0") : totalAmount;
      const remainingAmount = totalAmount - paidAmount;

      for (const poultry of selectedPoultry) {
        const unitPrice = typeof poultry.data.price === 'string'
          ? parseFloat(poultry.data.price.replace(/[^\d.-]/g, ''))
          : (poultry.data.price || 0);

        const notes = isCredit 
          ? `Vente à crédit - Client: ${customerName} (${customerContact}) - Adresse: ${customerAddress}`
          : `Paiement par ${paymentMethod}`;

        const { error } = await supabase
          .from('poultry_transactions')
          .insert([{
            transaction_type: 'sale',
            stock_item_id: poultry.id,
            quantity: poultry.selectedQuantity,
            unit_price: unitPrice,
            total_amount: unitPrice * poultry.selectedQuantity,
            transaction_date: new Date().toISOString(),
            created_by: user.id,
            paid_amount: paidAmount,
            remaining_amount: remainingAmount,
            is_fully_paid: !isCredit || remainingAmount === 0,
            payment_due_date: isCredit ? paymentDate : null,
            digital_signature: isCredit ? customerSignature : null,
            notes: notes,
          }]);

        if (error) throw error;

        const { error: stockError } = await supabase
          .from('stock_items')
          .update({ 
            quantity: poultry.quantity - poultry.selectedQuantity 
          })
          .eq('id', poultry.id);

        if (stockError) throw stockError;
      }

      toast.success("Transaction enregistrée avec succès");
      resetForm();
      onSuccess();
    } catch (error: any) {
      console.error("Erreur lors de l'ajout de la transaction:", error);
      toast.error(error.message || "Erreur lors de l'ajout de la transaction");
    }
  };

  const resetForm = () => {
    setSelectedPoultry([]);
    setIsCredit(false);
    setPartialPayment("");
    setCustomerName("");
    setCustomerContact("");
    setPaymentDate("");
    setCustomerSignature("");
    setCustomerAddress("");
    setPaymentMethod("");
  };

  return {
    selectedPoultry,
    setSelectedPoultry,
    isCredit,
    setIsCredit,
    partialPayment,
    setPartialPayment,
    customerName,
    setCustomerName,
    customerContact,
    setCustomerContact,
    paymentDate,
    setPaymentDate,
    customerSignature,
    setCustomerSignature,
    customerAddress,
    setCustomerAddress,
    paymentMethod,
    setPaymentMethod,
    calculateTotal,
    handleAddTransaction,
    resetForm
  };
};